"use client";
import { Box } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { Text, Link, List } from "@nxt-costco-com/forge-components";
import { SpaceXl, FontSize100, ColorTextWhite, SpaceLg, SpaceMd, ColorRed500, SpaceSm, SpaceXs, SizeBorderWidthMedium, ColorTextDefault, ColorWhite, ButtonsRadius, ColorGray200, ColorBlack, FontSize200, ColorGray500, RadiusSm, ColorGray900, FontLineHeightSm, FontWeightBold, FontSize400, FontLineHeightMd, FontWeightDefault, FontSize300 } from "@nxt-costco-com/forge-design-tokens";
import GenericImageHandler from "../../Util/GenericImageHandler.js";
const HEADER_HEIGHT = 250;
const HEADER_ICON_HEIGHT = 72;
const HEADER_ICON_WIDTH = 72;
const DEFAULT_FEATURE_BACKGROUND_COLOR = ColorRed500;
const DEFAULT_FEATURE_TEXT_COLOR = ColorWhite;
const MAX_TILE_WIDTH = 448;
const textStyling = {
  bodyText: {
    fontSize: FontSize300,
    fontWeight: FontWeightDefault,
    lineHeight: FontLineHeightMd
  },
  disclaimer: {
    fontSize: FontSize100,
    fontWeight: FontWeightDefault,
    lineHeight: FontLineHeightMd
  },
  subtitle: {
    fontSize: FontSize400,
    fontWeight: FontWeightBold,
    lineHeight: FontLineHeightSm
  },
  title: {
    fontSize: "1.625rem",
    fontWeight: FontWeightBold,
    lineHeight: FontLineHeightSm
  }
};
const getHeaderBackgroundStyle = (backgroundImageUrl, gradientStyle, singleBackgroundColor, gradientColors) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  let background = "";
  if (backgroundImageUrl) {
    background = `url(${backgroundImageUrl})`;
  }
  switch (gradientStyle) {
    case "no-background":
      return { backgroundImage: background };
    case "solid":
      background = background ? `${background}, ${singleBackgroundColor == null ? void 0 : singleBackgroundColor.hex}` : singleBackgroundColor == null ? void 0 : singleBackgroundColor.hex;
      break;
    case "linear":
      background = background ? `${background}, linear-gradient(${(_a = gradientColors == null ? void 0 : gradientColors.color1) == null ? void 0 : _a.hex}, ${(_b = gradientColors == null ? void 0 : gradientColors.color2) == null ? void 0 : _b.hex})` : `linear-gradient(${(_c = gradientColors == null ? void 0 : gradientColors.color1) == null ? void 0 : _c.hex}, ${(_d = gradientColors == null ? void 0 : gradientColors.color2) == null ? void 0 : _d.hex})`;
      break;
    case "radial":
      background = background ? `${background}, radial-gradient(${(_e = gradientColors == null ? void 0 : gradientColors.color1) == null ? void 0 : _e.hex}, ${(_f = gradientColors == null ? void 0 : gradientColors.color2) == null ? void 0 : _f.hex})` : `radial-gradient(${(_g = gradientColors == null ? void 0 : gradientColors.color1) == null ? void 0 : _g.hex}, ${(_h = gradientColors == null ? void 0 : gradientColors.color2) == null ? void 0 : _h.hex})`;
      break;
  }
  return background ? { background } : null;
};
const StyledTileContainer = styled(Box)({
  display: "flex",
  height: "100%",
  width: "100%"
});
const CaptionButtonContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "contentAlignment"
})(
  ({ contentAlignment, hasCaption }) => ({
    // from tiles_button_dynamic_row_config.content_alignment; if nothing is passed default to left
    alignItems: contentAlignment || "flex-start",
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: hasCaption ? SpaceXl : "0",
    justifyContent: "flex-end"
  })
);
const CaptionTextStyle = styled(Text, {
  shouldForwardProp: (prop) => prop !== "color"
})(({ color }) => ({
  color: color || ColorTextWhite,
  fontSize: FontSize100
}));
const CardBody = styled(Box, {
  shouldForwardProp: (prop) => prop !== "background" && prop !== "color"
})(({ background, color }) => ({
  backgroundColor: background ? background : DEFAULT_FEATURE_BACKGROUND_COLOR,
  color,
  display: "flex",
  flexDirection: "column",
  gap: SpaceMd,
  height: "100%",
  padding: SpaceLg,
  width: "100%"
}));
const VideoStyles = (largeViewportFlexDirection, aspectRatio) => {
  const rowSmallStyles = {
    aspectRatio,
    height: "100%",
    width: "100%",
    maxWidth: "max-content",
    maxHeight: "max-content"
  };
  const rowLargeStyles = {
    width: "100%",
    height: "100%"
  };
  const columnStyles = {
    aspectRatio,
    height: "100%",
    width: "100%",
    maxWidth: "max-content",
    maxHeight: "max-content"
  };
  const largeStyles = largeViewportFlexDirection === "column" ? columnStyles : rowLargeStyles;
  const smallStyles = largeViewportFlexDirection === "column" ? columnStyles : rowSmallStyles;
  const theme = useTheme();
  return {
    [theme.breakpoints.up("md")]: {
      ...largeStyles
    },
    [theme.breakpoints.down("md")]: {
      ...smallStyles
    }
  };
};
const HeaderContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "alignItems" && prop !== "backgroundStyle" && prop !== "largeViewportFlexDirection"
})(({ alignItems, backgroundStyle, largeViewportFlexDirection, theme }) => ({
  ...backgroundStyle,
  alignItems: alignItems || "center",
  backgroundPosition: "center",
  backgroundSize: "cover",
  display: "flex",
  height: largeViewportFlexDirection === "column" ? HEADER_HEIGHT : "100%",
  justifyContent: "center",
  minHeight: HEADER_HEIGHT,
  width: "100%",
  [theme.breakpoints.between("xs", "md")]: {
    height: HEADER_HEIGHT
  }
}));
const TextSectionContainer = styled(Box)({
  alignContent: "center",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  textAlign: "center"
});
const TextContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "contentAlignment"
})(({ contentAlignment = "left" }) => ({
  display: "flex",
  flexDirection: "column",
  gap: SpaceMd,
  textAlign: contentAlignment
}));
const TilesContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "tileOrientation"
})(({ tileOrientation, theme }) => ({
  alignItems: "stretch",
  alignSelf: "stretch",
  display: "flex",
  flexDirection: tileOrientation,
  flexWrap: "wrap",
  gap: SpaceSm,
  height: "fit-content",
  justifyContent: "center",
  [theme.breakpoints.up("lg")]: {
    "& > div:first-of-type, & > div:last-of-type": {
      padding: `${SpaceSm} 0`
    }
  }
}));
const Tile = styled(Box, {
  shouldForwardProp: (prop) => prop !== "background" && prop !== "borderColor" && prop !== "color"
})(
  ({ background, borderColor, color }) => ({
    alignItems: "center",
    backgroundColor: background || "auto",
    border: borderColor ? `${SizeBorderWidthMedium} solid ${borderColor}` : "none",
    borderRadius: "4px",
    color: color || "auto",
    display: "flex",
    flex: "1 0 0",
    flexDirection: "column",
    gap: SpaceMd,
    padding: `${SpaceSm} ${SpaceXs}`,
    width: "100%",
    minWidth: `calc(33% - ${SpaceMd})`
  })
);
const LinkTile = styled(Link, {
  shouldForwardProp: (prop) => prop !== "background" && prop !== "borderColor" && prop !== "color"
})(
  ({ background, borderColor, color }) => ({
    alignItems: "center",
    backgroundColor: background || "auto",
    border: borderColor ? `${SizeBorderWidthMedium} solid ${borderColor}` : "none",
    borderRadius: "4px",
    color: color || ColorTextDefault,
    display: "flex",
    flex: "1 0 0",
    flexDirection: "column",
    gap: SpaceMd,
    padding: `${SpaceSm} ${SpaceXs}`,
    width: "100%",
    minWidth: `calc(33% - ${SpaceMd})`
  })
);
const TileImage = styled(GenericImageHandler, {
  shouldForwardProp: () => true
})({
  height: "50px",
  objectFit: "contain",
  width: "50px"
});
const TileTextContainer = styled(Box)({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  textAlign: "center"
});
const TitleText = styled(Text, {
  shouldForwardProp: (prop) => prop !== "color"
})(({ color }) => ({
  color: color || ColorTextDefault
}));
const SubtitleText = styled(Text, {
  shouldForwardProp: (prop) => prop !== "color"
})(({ color }) => ({
  color: color || ColorTextWhite
}));
const OverlayIconContainer = styled(Box)({
  alignItems: "center",
  backgroundColor: ColorWhite,
  borderRadius: "100px",
  display: "flex",
  height: HEADER_ICON_HEIGHT,
  justifyContent: "center",
  padding: SpaceLg,
  width: HEADER_ICON_WIDTH
});
const CardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "largeViewportFlexDirection"
})(
  ({ largeViewportFlexDirection, theme }) => ({
    "@media (prefers-reduced-motion)": {
      transform: "none"
    },
    alignItems: "flex-start",
    borderRadius: "10px",
    display: "flex",
    flexDirection: largeViewportFlexDirection,
    overflow: "hidden",
    textDecoration: "none",
    width: "100%",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column"
    }
  })
);
const BelowFeatureTilesButton = styled(Link, {
  shouldForwardProp: (prop) => prop !== "background" && prop !== "borderColor" && prop !== "color"
})(({ background, color }) => ({
  "&:hover, &:focus": {
    color: ColorBlack,
    textDecoration: "none",
    backgroundColor: ColorGray200
  },
  alignItems: "center",
  background: background || ColorWhite,
  border: "none",
  borderRadius: ButtonsRadius,
  color: color || ColorTextDefault,
  display: "flex",
  justifyContent: "flex",
  padding: `10px ${SpaceMd} `,
  height: "40px",
  width: "fit-content"
}));
const ButtonRow = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column"
  },
  ":only-child": {
    // !important used to apply over last-child:nth-child(odd) styling
    "a:only-child": {
      width: "fit-content"
    }
  },
  display: "flex",
  gap: SpaceMd,
  height: "fit-content"
}));
const ButtonGroupContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "contentAlignment"
})(({ contentAlignment }) => ({
  display: "flex",
  flexDirection: "column",
  gap: SpaceMd,
  width: "100%",
  alignItems: contentAlignment || "flex-start"
}));
const BGButton = styled(BelowFeatureTilesButton)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    width: "100%"
  },
  ":only-child": {
    width: "100%"
  },
  height: "100%",
  width: `calc(50% - ${SpaceXs})`
}));
const LegendImageBody = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: SpaceMd,
  [theme.breakpoints.up("lg")]: {
    flexDirection: "row",
    gap: SpaceXl
  }
}));
const InstructionText = styled(Text)({
  color: ColorTextDefault,
  fontSize: FontSize200,
  width: "100%"
});
const ImageWithLegendContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: SpaceMd,
  width: "fit-content",
  [theme.breakpoints.down("lg")]: {
    width: "100%"
  }
}));
const LegendTilesImageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "largeViewportFlexDirection"
})(
  ({ largeViewportFlexDirection, theme }) => ({
    "@media (prefers-reduced-motion)": {
      transform: "none"
    },
    alignItems: "flex-start",
    borderRadius: "10px",
    display: "flex",
    flexDirection: largeViewportFlexDirection,
    overflow: "hidden",
    textDecoration: "none",
    width: "100%",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column"
    }
  })
);
const LegendTilesContainer = styled(List)(({ theme }) => ({
  "& > li": {
    ":last-child": {
      borderBottom: `1px solid ${ColorGray500}`,
      [theme.breakpoints.up("lg")]: {
        border: "none"
      }
    },
    borderTop: `1px solid ${ColorGray500}`,
    width: "100%",
    [theme.breakpoints.up("lg")]: {
      border: "none"
    }
  },
  alignItems: "flex-start",
  alignSelf: "stretch",
  display: "flex",
  flexDirection: "column",
  margin: 0,
  [theme.breakpoints.up("lg")]: {
    "& .MuiDivider-root": {
      display: "none"
    },
    maxWidth: "25%"
  }
}));
const LegendItem = styled(Box)(({ theme }) => ({
  "& .MuiLink-root": {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    gap: SpaceXs,
    width: "100%",
    [theme.breakpoints.up("lg")]: {
      textDecoration: "underline",
      textDecorationColor: ColorBlack
    }
  },
  alignItems: "center",
  alignSelf: "stretch",
  display: "flex",
  gap: SpaceXs,
  padding: `${SpaceXs} ${SpaceXs} ${SpaceXs} 0`
}));
const SwatchWrapper = styled(Box)({
  alignItems: "center",
  border: `1px solid ${ColorGray900}`,
  borderRadius: RadiusSm,
  display: "flex",
  height: "28px",
  justifyContent: "center",
  width: "28px"
});
const LegendColor = styled(Box, {
  shouldForwardProp: (prop) => prop !== "backgroundColor"
})(({ backgroundColor }) => ({
  background: backgroundColor ? backgroundColor : ColorWhite,
  borderRadius: "1px",
  flexShrink: 1,
  height: SpaceLg,
  width: SpaceLg
}));
const StyledHeaderContainerSx = {
  display: "contents",
  "&:hover": {
    textDecoration: "none"
  }
};
export {
  BGButton,
  BelowFeatureTilesButton,
  ButtonGroupContainer,
  ButtonRow,
  CaptionButtonContainer,
  CaptionTextStyle,
  CardBody,
  CardContainer,
  DEFAULT_FEATURE_BACKGROUND_COLOR,
  DEFAULT_FEATURE_TEXT_COLOR,
  HEADER_HEIGHT,
  HEADER_ICON_HEIGHT,
  HEADER_ICON_WIDTH,
  HeaderContainer,
  ImageWithLegendContainer,
  InstructionText,
  LegendColor,
  LegendImageBody,
  LegendItem,
  LegendTilesContainer,
  LegendTilesImageContainer,
  LinkTile,
  MAX_TILE_WIDTH,
  OverlayIconContainer,
  StyledHeaderContainerSx,
  StyledTileContainer,
  SubtitleText,
  SwatchWrapper,
  TextContainer,
  TextSectionContainer,
  Tile,
  TileImage,
  TileTextContainer,
  TilesContainer,
  TitleText,
  VideoStyles,
  getHeaderBackgroundStyle,
  textStyling
};
